import React from 'react';
import { useIntl } from 'react-intl';
import type { AddOnOffer } from 'fym-shared/src/music24/limitUpsell';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../common/types';
import { formatAddOnCents } from '../../settings/tabs/addOns/addOnFormat';
import { SMLButton } from '../components/primitives/SMLButton';
import { planNameMessages } from '../config/music24PlanNames';
import { limitUpsellMessages as messages } from './limitUpsellMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the limit upsell; each part uses its own slice of it */
import styles from './LimitUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LimitUpsellConfirmProps {
  addOn: AddOnOffer;
  plan: Music24PlanTier | null;
  isAdding: boolean;
  hasFailed: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The short confirm of an add-on (design limit-upsell a2, a3, a5): the plan, the add-ons after the
 * click and the new total for one billing period, and that the charge of today is prorated.
 */
const LimitUpsellConfirmComponent: React.FC<LimitUpsellConfirmProps> = ({
  addOn,
  plan,
  isAdding,
  hasFailed,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();
  const period = (cents: number) =>
    intl.formatMessage(addOn.interval === 'yearly' ? messages.perYear : messages.perMonth, {
      price: formatAddOnCents(intl, cents),
    });
  const isPosts = addOn.kind === 'posts';
  const planCents = addOn.totalPeriodCents === null ? null : addOn.totalPeriodCents - addOn.addOnsPeriodCents;

  return (
    <div className={styles.confirm} data-testid="limit-upsell-confirm">
      <p className={styles.title}>
        {intl.formatMessage(isPosts ? messages.confirmPosts : messages.confirmCredits, { count: addOn.add })}
      </p>
      <div className={styles.rows}>
        {plan !== null && planCents !== null && (
          <>
            <span>{intl.formatMessage(planNameMessages[plan])}</span>
            <span className={styles.fig}>{formatAddOnCents(intl, planCents)}</span>
          </>
        )}
        <span>{intl.formatMessage(messages.rowAddOns)}</span>
        <span className={styles.fig}>{formatAddOnCents(intl, addOn.addOnsPeriodCents)}</span>
        {addOn.totalPeriodCents !== null && (
          <>
            <span className={styles.total}>{intl.formatMessage(messages.rowTotal)}</span>
            <span className={`${styles.fig} ${styles.total}`}>{period(addOn.totalPeriodCents)}</span>
          </>
        )}
      </div>
      <p className={styles.note}>{intl.formatMessage(messages.confirmNote)}</p>
      {hasFailed && (
        <div className={`${styles.alert} ${styles.alertBad}`} role="alert">
          {intl.formatMessage(messages.addFailed)}
        </div>
      )}
      <div className={styles.actions}>
        <SMLButton variant="primary" disabled={isAdding} onClick={onConfirm}>
          {intl.formatMessage(
            isAdding ? messages.adding : isPosts ? messages.confirmAddPosts : messages.confirmAddCredits,
            { count: addOn.add }
          )}
        </SMLButton>
        <button type="button" className={styles.quiet} disabled={isAdding} onClick={onCancel}>
          {intl.formatMessage(messages.cancel)}
        </button>
      </div>
    </div>
  );
};

export const LimitUpsellConfirm = TypedMemo(LimitUpsellConfirmComponent);
